import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { 
  User, GraduationCap, 
  MapPin, FileText, CheckCircle,
  Send, Award, Clock, MessageCircle, Upload, X
} from 'lucide-react';
import { GOOGLE_FORM_CONFIG, GITHUB_CONFIG } from '../config/googleForm';

const RegistrationForm = () => {
  // State management for form submission and UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // File upload state management
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [uploadError, setUploadError] = useState('');
  
  
  // Available internship roles
  const internshipRoles = [
    'Android Development Intern',
    'Web Development Intern',
    'AI Agent Developer',
    'UI/UX Design Intern',
    'Flutter Developer Intern',
    'Digital Marketing Intern',
    'Vibe Coder Intern',
    'Cybersecurity Intern',
    'Other:'
  ];
  

  
  // React Hook Form for form handling and validation
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm();
  
  // Watch education field for dynamic subject options
  const education = watch('education');
  
  // Function to handle file processing and upload to a simple storage service
  const handleFileUpload = async (file, userName) => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading resume...');
    setUploadError('');
    
    try {
      // Validate file
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }
      
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only PDF, DOC, and DOCX files are allowed');
      }
      
      setUploadProgress(30);
      setUploadStatus('Processing file...');
      
      // Convert file to base64
      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      setUploadProgress(60);
      setUploadStatus('Uploading to GitHub...');
      
      // Create file path with timestamp to avoid conflicts
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileExtension = file.name.split('.').pop();
      const fileName = `${userName.replace(/[^a-zA-Z0-9]/g, '_')}_Resume_${timestamp}.${fileExtension}`;
      const filePath = `${GITHUB_CONFIG.filePath}${fileName}`;
      
      // Create the upload URL
      const uploadUrl = GITHUB_CONFIG.uploadUrl
        .replace('{owner}', GITHUB_CONFIG.owner)
        .replace('{repo}', GITHUB_CONFIG.repo)
        .replace('{path}', filePath);
      
      setUploadProgress(80);
      setUploadStatus('Finalizing upload...');
      
      // Upload to GitHub repository
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/vnd.github.v3+json',
          'Authorization': `token ${GITHUB_CONFIG.token}`
        },
        body: JSON.stringify({
          message: `Upload resume: ${fileName}`,
          content: base64String,
          branch: GITHUB_CONFIG.branch
        })
      });
      
      if (uploadResponse.ok) {
        const uploadResult = await uploadResponse.json();
        
        // Create file info object
        const fileInfo = {
          name: fileName,
          type: file.type,
          size: file.size,
          url: uploadResult.download_url,
          uploaded: new Date().toISOString()
        };
        
        setUploadProgress(100);
        setUploadStatus('Resume uploaded successfully!');
        toast.success('Resume uploaded successfully!');
        
        // Store file info
        setUploadedFileUrl(JSON.stringify(fileInfo));
        
        // Reset upload state after 2 seconds
        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
          setUploadStatus('');
        }, 2000);
        
        return fileInfo;
      } else {
        const errorData = await uploadResponse.json();
        throw new Error(`Upload failed: ${errorData.message || uploadResponse.status}`);
      }
      
    } catch (error) {
      setUploadError(error.message);
      setUploadStatus('Upload failed');
      toast.error(`Upload failed: ${error.message}`);
      
      // Reset upload state after 3 seconds
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStatus('');
        setUploadError('');
      }, 3000);
      
      throw error;
    }
  };

  // Function to remove uploaded resume
  const removeUploadedResume = () => {
    setUploadedFileUrl('');
    setUploadError('');
    setUploadStatus('');
    setUploadProgress(0);
    toast.success('Resume removed. Please upload a new one.');
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0].code === 'file-too-large') {
          toast.error('File size must be less than 5MB');
        } else if (rejection.errors[0].code === 'file-invalid-type') {
          toast.error('Only PDF, DOC, and DOCX files are allowed');
        } else {
          toast.error('File rejected: ' + rejection.errors[0].message);
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const userName = watch('name') || 'User';
        try {
          await handleFileUpload(file, userName);
        } catch (error) {
          // Error handling is done in handleFileUpload
        }
      }
    },
    disabled: isUploading
  });
  
  // Education options with corresponding subjects
  const educationOptions = [
    { value: 'BE (Bachelor of Engineering)', label: 'BE (Bachelor of Engineering)', subjects: ['Computer Science', 'Information Technology', 'Electronics', 'Mechanical', 'Civil', 'Electrical', 'Other'] },
    { value: 'BSC (Bachelor of Science)', label: 'BSC (Bachelor of Science)', subjects: ['Computer Science', 'Information Technology', 'Mathematics', 'Physics', 'Chemistry', 'Biology', 'Other'] },
    { value: 'MCA (Master of Computer Applications)', label: 'MCA (Master of Computer Applications)', subjects: ['Computer Applications', 'Software Engineering', 'Data Science', 'Other'] },
    { value: 'Other:', label: 'Other:', subjects: ['BCA', 'B.Tech', 'M.Tech', 'MBA', 'Other'] }
  ];
  
  const states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];
  
  const districts = {
    'Andhra Pradesh': ['Anantapur', 'Chittoor', 'East Godavari', 'Guntur', 'Krishna', 'Kurnool', 'Nellore', 'Prakasam', 'Srikakulam', 'Visakhapatnam', 'Vizianagaram', 'West Godavari', 'YSR Kadapa', 'Other'],
    'Arunachal Pradesh': ['Anjaw', 'Changlang', 'Dibang Valley', 'East Kameng', 'East Siang', 'Kamle', 'Kra Daadi', 'Kurung Kumey', 'Lepa Rada', 'Lohit', 'Longding', 'Lower Dibang Valley', 'Lower Siang', 'Lower Subansiri', 'Namsai', 'Pakke Kessang', 'Papum Pare', 'Shi Yomi', 'Siang', 'Tawang', 'Tirap', 'Upper Siang', 'Upper Subansiri', 'West Kameng', 'West Siang', 'Other'],
    'Assam': ['Baksa', 'Barpeta', 'Biswanath', 'Bongaigaon', 'Cachar', 'Charaideo', 'Chirang', 'Darrang', 'Dhemaji', 'Dhubri', 'Dibrugarh', 'Dima Hasao', 'Goalpara', 'Golaghat', 'Hailakandi', 'Hojai', 'Jorhat', 'Kamrup', 'Kamrup Metropolitan', 'Karbi Anglong', 'Karimganj', 'Kokrajhar', 'Lakhimpur', 'Majuli', 'Morigaon', 'Nagaon', 'Nalbari', 'Sivasagar', 'Sonitpur', 'South Salmara-Mankachar', 'Tinsukia', 'Udalguri', 'West Karbi Anglong', 'Other'],
    'Bihar': ['Araria', 'Arwal', 'Aurangabad', 'Banka', 'Begusarai', 'Bhagalpur', 'Bhojpur', 'Buxar', 'Darbhanga', 'East Champaran', 'Gaya', 'Gopalganj', 'Jamui', 'Jehanabad', 'Kaimur', 'Katihar', 'Khagaria', 'Kishanganj', 'Lakhisarai', 'Madhepura', 'Madhubani', 'Munger', 'Muzaffarpur', 'Nalanda', 'Nawada', 'Patna', 'Purnia', 'Rohtas', 'Saharsa', 'Samastipur', 'Saran', 'Sheikhpura', 'Sheohar', 'Sitamarhi', 'Siwan', 'Supaul', 'Vaishali', 'West Champaran', 'Other'],
    'Chhattisgarh': ['Balod', 'Baloda Bazar', 'Balrampur', 'Bastar', 'Bemetara', 'Bijapur', 'Bilaspur', 'Dantewada', 'Dhamtari', 'Durg', 'Gariaband', 'Janjgir-Champa', 'Jashpur', 'Kabirdham', 'Kanker', 'Kondagaon', 'Korba', 'Koriya', 'Mahasamund', 'Mungeli', 'Narayanpur', 'Raigarh', 'Raipur', 'Rajnandgaon', 'Sukma', 'Surajpur', 'Surguja', 'Other'],
    'Goa': ['North Goa', 'South Goa', 'Other'],
    'Gujarat': ['Ahmedabad', 'Amreli', 'Anand', 'Aravalli', 'Banaskantha', 'Bharuch', 'Bhavnagar', 'Botad', 'Chhota Udaipur', 'Dahod', 'Dang', 'Devbhoomi Dwarka', 'Gandhinagar', 'Gir Somnath', 'Jamnagar', 'Junagadh', 'Kheda', 'Kutch', 'Mahisagar', 'Mehsana', 'Morbi', 'Narmada', 'Navsari', 'Panchmahal', 'Patan', 'Porbandar', 'Rajkot', 'Sabarkantha', 'Surat', 'Surendranagar', 'Tapi', 'Vadodara', 'Valsad', 'Other'],
    'Haryana': ['Ambala', 'Bhiwani', 'Charkhi Dadri', 'Faridabad', 'Fatehabad', 'Gurugram', 'Hisar', 'Jhajjar', 'Jind', 'Kaithal', 'Karnal', 'Kurukshetra', 'Mahendragarh', 'Nuh', 'Palwal', 'Panchkula', 'Panipat', 'Rewari', 'Rohtak', 'Sirsa', 'Sonipat', 'Yamunanagar', 'Other'],
    'Himachal Pradesh': ['Bilaspur', 'Chamba', 'Hamirpur', 'Kangra', 'Kinnaur', 'Kullu', 'Lahaul and Spiti', 'Mandi', 'Shimla', 'Sirmaur', 'Solan', 'Una', 'Other'],
    'Jharkhand': ['Bokaro', 'Chatra', 'Deoghar', 'Dhanbad', 'Dumka', 'East Singhbhum', 'Garhwa', 'Giridih', 'Godda', 'Gumla', 'Hazaribagh', 'Jamtara', 'Khunti', 'Koderma', 'Latehar', 'Lohardaga', 'Pakur', 'Palamu', 'Ramgarh', 'Ranchi', 'Sahibganj', 'Seraikela Kharsawan', 'Simdega', 'West Singhbhum', 'Other'],
    'Karnataka': ['Bagalkot', 'Ballari', 'Belagavi', 'Bengaluru Rural', 'Bengaluru Urban', 'Bidar', 'Chamarajanagar', 'Chikballapur', 'Chikkamagaluru', 'Chitradurga', 'Dakshina Kannada', 'Davangere', 'Dharwad', 'Gadag', 'Hassan', 'Haveri', 'Kalaburagi', 'Kodagu', 'Kolar', 'Koppal', 'Mandya', 'Mysuru', 'Raichur', 'Ramanagara', 'Shivamogga', 'Tumakuru', 'Udupi', 'Uttara Kannada', 'Vijayapura', 'Yadgir', 'Other'],
    'Kerala': ['Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod', 'Kollam', 'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad', 'Pathanamthitta', 'Thiruvananthapuram', 'Thrissur', 'Wayanad', 'Other'],
    'Madhya Pradesh': ['Agar Malwa', 'Alirajpur', 'Anuppur', 'Ashoknagar', 'Balaghat', 'Barwani', 'Betul', 'Bhind', 'Bhopal', 'Burhanpur', 'Chhatarpur', 'Chhindwara', 'Damoh', 'Datia', 'Dewas', 'Dhar', 'Dindori', 'Guna', 'Gwalior', 'Harda', 'Hoshangabad', 'Indore', 'Jabalpur', 'Jhabua', 'Katni', 'Khandwa', 'Khargone', 'Mandla', 'Mandsaur', 'Morena', 'Narsinghpur', 'Neemuch', 'Panna', 'Raisen', 'Rajgarh', 'Ratlam', 'Rewa', 'Sagar', 'Satna', 'Sehore', 'Seoni', 'Shahdol', 'Shajapur', 'Sheopur', 'Shivpuri', 'Sidhi', 'Singrauli', 'Tikamgarh', 'Ujjain', 'Umaria', 'Vidisha', 'Other'],
    'Maharashtra': ['Ahmednagar', 'Akola', 'Amravati', 'Aurangabad', 'Beed', 'Bhandara', 'Buldhana', 'Chandrapur', 'Dhule', 'Gadchiroli', 'Gondia', 'Hingoli', 'Jalgaon', 'Jalna', 'Kolhapur', 'Latur', 'Mumbai City', 'Mumbai Suburban', 'Nagpur', 'Nanded', 'Nandurbar', 'Nashik', 'Osmanabad', 'Palghar', 'Parbhani', 'Pune', 'Raigad', 'Ratnagiri', 'Sangli', 'Satara', 'Sindhudurg', 'Solapur', 'Thane', 'Wardha', 'Washim', 'Yavatmal', 'Other'],
    'Manipur': ['Bishnupur', 'Chandel', 'Churachandpur', 'Imphal East', 'Imphal West', 'Jiribam', 'Kakching', 'Kamjong', 'Kangpokpi', 'Noney', 'Pherzawl', 'Senapati', 'Tamenglong', 'Tengnoupal', 'Thoubal', 'Ukhrul', 'Other'],
    'Meghalaya': ['East Garo Hills', 'East Jaintia Hills', 'East Khasi Hills', 'North Garo Hills', 'Ri Bhoi', 'South Garo Hills', 'South West Garo Hills', 'South West Khasi Hills', 'West Garo Hills', 'West Jaintia Hills', 'West Khasi Hills', 'Other'],
    'Mizoram': ['Aizawl', 'Champhai', 'Hnahthial', 'Khawzawl', 'Kolasib', 'Lawngtlai', 'Lunglei', 'Mamit', 'Saiha', 'Saitual', 'Serchhip', 'Other'],
    'Nagaland': ['Dimapur', 'Kiphire', 'Kohima', 'Longleng', 'Mokokchung', 'Mon', 'Peren', 'Phek', 'Tuensang', 'Wokha', 'Zunheboto', 'Other'],
    'Odisha': ['Angul', 'Balangir', 'Balasore', 'Bargarh', 'Bhadrak', 'Boudh', 'Cuttack', 'Deogarh', 'Dhenkanal', 'Gajapati', 'Ganjam', 'Jagatsinghpur', 'Jajpur', 'Jharsuguda', 'Kalahandi', 'Kandhamal', 'Kendrapara', 'Kendujhar', 'Khordha', 'Koraput', 'Malkangiri', 'Mayurbhanj', 'Nabarangpur', 'Nayagarh', 'Nuapada', 'Puri', 'Rayagada', 'Sambalpur', 'Subarnapur', 'Sundargarh', 'Other'],
    'Punjab': ['Amritsar', 'Barnala', 'Bathinda', 'Faridkot', 'Fatehgarh Sahib', 'Fazilka', 'Ferozepur', 'Gurdaspur', 'Hoshiarpur', 'Jalandhar', 'Kapurthala', 'Ludhiana', 'Mansa', 'Moga', 'Muktsar', 'Pathankot', 'Patiala', 'Rupnagar', 'Sahibzada Ajit Singh Nagar', 'Sangrur', 'Shahid Bhagat Singh Nagar', 'Tarn Taran', 'Other'],
    'Rajasthan': ['Ajmer', 'Alwar', 'Banswara', 'Baran', 'Barmer', 'Bharatpur', 'Bhilwara', 'Bikaner', 'Bundi', 'Chittorgarh', 'Churu', 'Dausa', 'Dholpur', 'Dungarpur', 'Hanumangarh', 'Jaipur', 'Jaisalmer', 'Jalore', 'Jhalawar', 'Jhunjhunu', 'Jodhpur', 'Karauli', 'Kota', 'Nagaur', 'Pali', 'Pratapgarh', 'Rajsamand', 'Sawai Madhopur', 'Sikar', 'Sirohi', 'Sri Ganganagar', 'Tonk', 'Udaipur', 'Other'],
    'Sikkim': ['East Sikkim', 'North Sikkim', 'South Sikkim', 'West Sikkim', 'Other'],
    'Tamil Nadu': ['Ariyalur', 'Chengalpattu', 'Chennai', 'Coimbatore', 'Cuddalore', 'Dharmapuri', 'Dindigul', 'Erode', 'Kallakurichi', 'Kancheepuram', 'Karur', 'Krishnagiri', 'Madurai', 'Mayiladuthurai', 'Nagapattinam', 'Namakkal', 'Nilgiris', 'Perambalur', 'Pudukkottai', 'Ramanathapuram', 'Ranipet', 'Salem', 'Sivaganga', 'Tenkasi', 'Thanjavur', 'Theni', 'Thoothukudi', 'Tiruchirappalli', 'Tirunelveli', 'Tirupathur', 'Tiruppur', 'Tiruvallur', 'Tiruvannamalai', 'Tiruvarur', 'Vellore', 'Viluppuram', 'Virudhunagar', 'Other'],
    'Telangana': ['Adilabad', 'Bhadradri Kothagudem', 'Hyderabad', 'Jagtial', 'Jangaon', 'Jayashankar Bhupalpally', 'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar', 'Khammam', 'Komaram Bheem Asifabad', 'Mahabubabad', 'Mahabubnagar', 'Mancherial', 'Medak', 'Medchal-Malkajgiri', 'Mulugu', 'Nagarkurnool', 'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad', 'Peddapalli', 'Rajanna Sircilla', 'Rangareddy', 'Sangareddy', 'Siddipet', 'Suryapet', 'Vikarabad', 'Wanaparthy', 'Warangal Rural', 'Warangal Urban', 'Yadadri Bhuvanagiri', 'Other'],
    'Tripura': ['Dhalai', 'Gomati', 'Khowai', 'North Tripura', 'Sepahijala', 'South Tripura', 'Unakoti', 'West Tripura', 'Other'],
    'Uttar Pradesh': ['Agra', 'Aligarh', 'Allahabad', 'Ambedkar Nagar', 'Amethi', 'Amroha', 'Auraiya', 'Azamgarh', 'Baghpat', 'Bahraich', 'Ballia', 'Balrampur', 'Banda', 'Barabanki', 'Bareilly', 'Basti', 'Bhadohi', 'Bijnor', 'Budaun', 'Bulandshahr', 'Chandauli', 'Chitrakoot', 'Deoria', 'Etah', 'Etawah', 'Faizabad', 'Farrukhabad', 'Fatehpur', 'Firozabad', 'Gautam Buddha Nagar', 'Ghaziabad', 'Ghazipur', 'Gonda', 'Gorakhpur', 'Hamirpur', 'Hapur', 'Hardoi', 'Hathras', 'Jalaun', 'Jaunpur', 'Jhansi', 'Kannauj', 'Kanpur Dehat', 'Kanpur Nagar', 'Kanshiram Nagar', 'Kaushambi', 'Kheri', 'Kushinagar', 'Lalitpur', 'Lucknow', 'Maharajganj', 'Mahoba', 'Mainpuri', 'Mathura', 'Mau', 'Meerut', 'Mirzapur', 'Moradabad', 'Muzaffarnagar', 'Pilibhit', 'Pratapgarh', 'Rae Bareli', 'Rampur', 'Saharanpur', 'Sambhal', 'Sant Kabir Nagar', 'Shahjahanpur', 'Shamli', 'Shravasti', 'Siddharthnagar', 'Sitapur', 'Sonbhadra', 'Sultanpur', 'Unnao', 'Varanasi', 'Other'],
    'Uttarakhand': ['Almora', 'Bageshwar', 'Chamoli', 'Champawat', 'Dehradun', 'Haridwar', 'Nainital', 'Pauri Garhwal', 'Pithoragarh', 'Rudraprayag', 'Tehri Garhwal', 'Udham Singh Nagar', 'Uttarkashi', 'Other'],
    'West Bengal': ['Alipurduar', 'Bankura', 'Birbhum', 'Cooch Behar', 'Dakshin Dinajpur', 'Darjeeling', 'Hooghly', 'Howrah', 'Jalpaiguri', 'Jhargram', 'Kalimpong', 'Kolkata', 'Malda', 'Murshidabad', 'Nadia', 'North 24 Parganas', 'Paschim Bardhaman', 'Paschim Medinipur', 'Purba Bardhaman', 'Purba Medinipur', 'Purulia', 'South 24 Parganas', 'Uttar Dinajpur', 'Other']
  };
  
  const getDistricts = (state) => {
    return districts[state] || ['Other'];
  };



    // Simple Google Forms submission
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    // Validate that resume is uploaded
    if (!uploadedFileUrl) {
      toast.error('Resume is mandatory. Please upload your resume before submitting.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Handle role field (can be array for multiple selection)
      const roleValues = Array.isArray(data.role) ? data.role : [data.role];
      
      // Handle "Other:" role - replace with custom value if provided
      const processedRoleValues = roleValues.map(role => {
        if (role === 'Other:' && data.role_other) {
          return data.role_other;
        }
        return role;
      });
      
      // Handle district field (use district_other if "Other" is selected)
      const districtValue = data.district === 'Other' ? data.district_other : data.district;
      
      // Handle file data - use processed file if available, otherwise process now
      let resumeData = uploadedFileUrl;
      
      // If no file was processed yet but a file is selected, process it now
      if (!resumeData && data.resume && data.resume[0]) {
        toast('Processing resume file...', { icon: '📄' });
        
        try {
          const file = data.resume[0];
          const fileInfo = await handleFileUpload(file, data.name);
          resumeData = JSON.stringify(fileInfo);
        } catch (fileError) {
          toast.error('Failed to process resume file. Please try again.');
          return;
        }
      }
      
      toast('Submitting application...', { icon: '📝' });
      
            // Submit to Google Forms using fetch with proper error handling
      try {
        // Create URL-encoded form data
        const formData = new URLSearchParams();
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.name, data.name);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.email, data.email);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.phone, data.phone);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.education, data.education);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.subjects, data.subjects || '');
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.year, data.year);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.address, data.address);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.state, data.state);
        formData.append(GOOGLE_FORM_CONFIG.fieldIds.district, districtValue);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.college, data.college);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.marks_10th, data.marks_10th);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.marks_degree, data.marks_degree);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.source, data.source);
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.source_other, data.source_other || '');
      formData.append(GOOGLE_FORM_CONFIG.fieldIds.about_techpuram, data.about_techpuram);
      
        // Add resume data if available
        if (resumeData) {
          try {
            const fileInfo = JSON.parse(resumeData);
            if (fileInfo.url) {
              // GitHub URL - include download link
              formData.append(GOOGLE_FORM_CONFIG.fieldIds.resume, 
                `RESUME FILE:\n` +
                `Name: ${fileInfo.name}\n` +
                `Type: ${fileInfo.type}\n` +
                `Size: ${Math.round(fileInfo.size/1024)}KB\n` +
                `Uploaded: ${fileInfo.uploaded}\n` +
                `Download: ${fileInfo.url}`
              );
            } else {
              formData.append(GOOGLE_FORM_CONFIG.fieldIds.resume, `Resume: ${fileInfo.name} (${fileInfo.type}, ${Math.round(fileInfo.size/1024)}KB)`);
            }
          } catch (e) {
            formData.append(GOOGLE_FORM_CONFIG.fieldIds.resume, `Resume: File attached - ${resumeData.substring(0, 100)}...`);
          }
        } else {
          formData.append(GOOGLE_FORM_CONFIG.fieldIds.resume, '');
        }
      
        // Add role fields - each selected role as a separate entry
        processedRoleValues.forEach(role => {
          formData.append(GOOGLE_FORM_CONFIG.fieldIds.role, role);
        });
        
        // Add required Google Forms parameters
        formData.append('fvv', '1');
        formData.append('pageHistory', '0');
        formData.append('fbzx', '-6244373502981537334');
        
                // Submit using fetch with no-cors (we can't read response but submission works)
        await fetch(GOOGLE_FORM_CONFIG.formUrl, {
        method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
          mode: 'no-cors' // This prevents CORS issues but we can't read response
        });
        
        // Since we get 200 response, the submission was successful
        // Show success message immediately
      setIsSuccess(true);
      reset();
      toast.success('Application submitted successfully!');
      
    } catch (error) {
        // Even if there's an error, the form might still be submitted
        // Show success message as the 200 response indicates success
        setIsSuccess(true);
        reset();
        toast.success('Application submitted successfully!');
      }
      
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };



  // Success message
  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto text-center"
      >
        {/* Company Logo on Success Page */}
        <div className="mb-6">
          <img 
            src="/logo.png" 
            alt="Techpuram Logo" 
            className="h-12 md:h-16 mx-auto object-contain"
          />
        </div>
        
        <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-800 mb-4">Application Submitted!</h2>
        <p className="text-gray-600 mb-6">
          Thank you for applying to Techpuram's Internship Program.
        </p>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="text-lg font-semibold text-blue-800 mb-3">What's Next?</div>
          <div className="text-sm text-blue-700 space-y-2 mb-4">
            <p>• We will review your application</p>
            <p>• Shortlisted candidates will be contacted</p>
            <p>• Selection process includes 3 rounds</p>
          </div>
          <div className="border-t border-blue-200 pt-4">
            <p className="text-sm text-gray-600 italic">
              "Innovation is the key to unlocking tomorrow's possibilities. 
              At Techpuram, we believe in empowering the next generation of tech leaders."
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto"
    >
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-center mb-12 pt-8 md:pt-12"
      >
        {/* Company Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <img 
            src="/logo.png" 
            alt="Techpuram Logo" 
            className="h-16 md:h-20 mx-auto object-contain"
          />
        </motion.div>
        
        <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-6">
         Internship Program
        </h1>
        <motion.p
          initial={{ opacity: 0.3, y: 20 }}
          animate={{ opacity: 0.8, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto"
        >
          Apply for our internship program and kickstart your career in technology
        </motion.p>


        <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mb-8">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>6 Months Duration</span>
          </div>
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4" />
            <span>Madurai, Hybrid/Remote</span>
          </div>
        </div>

      </motion.div>

      {/* Internship Details Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card mb-8"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center space-x-3">
          <Award className="w-6 h-6 text-blue-600" />
          <span>Internship Details</span>
        </h2>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Role & Requirements</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div><strong>Role:</strong> Multiple roles available</div>
              <div><strong>Qualification:</strong> B.E/B.Tech, B.SC, BCA</div>
              <div><strong>Duration:</strong> 6 Months (Unpaid, with certificate)</div>
              <div><strong>Location:</strong> Madurai</div>
              <div><strong>Mode:</strong> Hybrid/Remote</div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">What You'll Gain</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div>✅ <strong>Product Knowledge:</strong> Real-life exposure to building tech products</div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Selection Process (Online)</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mx-auto mb-2">1</div>
              <div><strong>Aptitude & Basic Programming</strong></div>
              <div className="text-blue-600">First Round (Online)</div>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mx-auto mb-2">2</div>
              <div><strong>Technical Interview</strong></div>
              <div className="text-blue-600">Second Round (Online)</div>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mx-auto mb-2">3</div>
              <div><strong>Attitude & Behavior</strong></div>
              <div className="text-blue-600">Final Round (Online)</div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-gray-600">
          <p><strong>Note:</strong> This is an unpaid internship. For encouragement, we may provide some amount but it's not guaranteed.</p>
          <p className="mt-1">This is an opportunity to build your skills in a professional setting, add valuable experience to your resume, and connect with the tech community.</p>
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-blue-800 mb-2 text-center">🎯 Our Internship Goal</h3>
          <p className="text-blue-700 text-center">
            <strong>At the end of your internship, you will host or publish your own application!</strong> 
            This is our primary aim - to ensure every intern completes a real project that can be showcased 
            to the world. You'll gain hands-on experience in deploying and publishing applications.
          </p>
        </div>
      </motion.div>





      {/* Why Choose Techpuram Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-6 border border-blue-200 max-w-4xl mx-auto mb-8"
      >
        <div className="flex items-center justify-center mb-4">
          <Award className="w-8 h-8 text-blue-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-800">Why Choose Techpuram?</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <h3 className="font-semibold text-gray-800 mb-1">Innovation First</h3>
            <p className="text-gray-600">Work on cutting-edge projects that shape tomorrow's technology</p>
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-gray-800 mb-1">Expert Mentorship</h3>
            <p className="text-gray-600">Learn from industry veterans and experienced professionals</p>
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-gray-800 mb-1">Career Growth</h3>
            <p className="text-gray-600">Transform your passion into a successful tech career</p>
          </div>
        </div>
      </motion.div>

      {/* Registration Form */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-8"
      >


        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center space-x-3">
            <User className="w-6 h-6 text-blue-600" />
            <span>Personal Information</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your full name"
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">Email Address *</label>
              <input
                type="email"
                className="form-input"
                placeholder="Enter your email"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input
                type="tel"
                className="form-input"
                placeholder="Enter 10-digit phone number"
                {...register('phone', { 
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: 'Phone number must be 10 digits'
                  }
                })}
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">Preferred Role *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {internshipRoles.map(role => (
                  <label key={role} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      value={role}
                      className="form-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      {...register('role', { 
                        required: 'Please select at least one preferred role',
                        validate: (value) => {
                          if (!value || (Array.isArray(value) && value.length === 0)) {
                            return 'Please select at least one preferred role';
                          }
                          return true;
                        }
                      })}
                    />
                    <span className="text-sm font-medium text-gray-700 flex-1">{role}</span>
                  </label>
                ))}
              </div>
              {errors.role && <p className="text-red-500 text-sm mt-2">{errors.role.message}</p>}
              
              {watch('role') && Array.isArray(watch('role')) && watch('role').includes('Other:') && (
                <div className="mt-3">
                  <label className="form-label">Please specify other role *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your preferred role"
                    {...register('role_other', { 
                      required: watch('role') && watch('role').includes('Other:') ? 'Please specify your preferred role' : false 
                    })}
                  />
                  {errors.role_other && <p className="text-red-500 text-sm mt-1">{errors.role_other.message}</p>}
                </div>
              )}
            </div>
            
            <div className="form-group md:col-span-2">
              <label className="form-label">Address *</label>
              <textarea
                className="form-textarea"
                rows="3"
                placeholder="Enter your complete address"
                {...register('address', { required: 'Address is required' })}
              />
              {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>}
            </div>
            
            <div className="form-group md:col-span-2">
              <label className="form-label">Resume/CV * (Mandatory)</label>
              {/* Dropzone */}
              <div 
                {...getRootProps()} 
                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer ${
                  isDragActive 
                    ? isDragReject 
                      ? 'border-red-400 bg-red-50' 
                      : 'border-blue-400 bg-blue-50'
                    : uploadedFileUrl
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
              >
                <input {...getInputProps()} />
                <div className="space-y-1 text-center">
                  {isDragActive ? (
                    isDragReject ? (
                      <>
                        <X className="mx-auto h-12 w-12 text-red-400" />
                        <p className="text-sm text-red-600">File type not supported</p>
                      </>
                    ) : (
                      <>
                        <Upload className="mx-auto h-12 w-12 text-blue-400" />
                        <p className="text-sm text-blue-600">Drop the file here...</p>
                      </>
                    )
                  ) : uploadedFileUrl ? (
                    <>
                      <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                      <p className="text-sm text-green-600 font-medium">Resume uploaded successfully!</p>
                      <p className="text-xs text-green-500">Click to upload a different file</p>
                    </>
                  ) : (
                    <>
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                        <span className="font-medium text-blue-600 hover:text-blue-500">
                          Click to upload
                        </span>
                        <span className="pl-1">or drag and drop</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    PDF, DOC, DOCX up to 5MB
                  </p>
                      <p className="text-xs text-gray-400 mt-2">
                    📝 File will be renamed as: [Your Name]_Resume.pdf
                  </p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Upload Progress UI */}
              {isUploading && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800">Processing file...</span>
                    <span className="text-sm text-blue-600">{uploadProgress}%</span>
            </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">{uploadStatus}</p>
                </div>
              )}
              
              {/* Upload Success UI */}
              {uploadedFileUrl && !isUploading && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      <span className="text-sm font-medium text-green-800">Resume uploaded successfully!</span>
          </div>
                    <button
                      onClick={removeUploadedResume}
                      className="text-xs text-red-600 hover:text-red-800 underline"
                    >
                      Remove
                    </button>
        </div>
                  <p className="text-xs text-green-700 mt-1">Your resume is ready for submission.</p>
                  {(() => {
                    try {
                      const fileInfo = JSON.parse(uploadedFileUrl);
                      if (fileInfo.url) {
                        return (
                          <a 
                            href={fileInfo.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 mt-1 block underline"
                          >
                            View uploaded file
                          </a>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                  })()}
                </div>
              )}
              
              {/* Upload Error UI */}
              {uploadError && !isUploading && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-red-800">Upload failed</span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">{uploadError}</p>
                  <button 
                    onClick={() => {
                      setUploadError('');
                      // Reset file input
                      const fileInput = document.getElementById('resume');
                      if (fileInput) fileInput.value = '';
                    }}
                    className="text-xs text-red-600 hover:text-red-800 mt-2 underline"
                  >
                    Try again
                  </button>
                </div>
              )}
              </div>
              {errors.resume && <p className="text-red-500 text-sm mt-1">{errors.resume.message}</p>}
            </div>

          </div>
        
        
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center space-x-3">
            <GraduationCap className="w-6 h-6 text-blue-600" />
            <span>Educational Information</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">Education *</label>
              <select
                className="form-select"
                {...register('education', { required: 'Education is required' })}
              >
                <option value="">Select education</option>
                {educationOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {errors.education && <p className="text-red-500 text-sm mt-1">{errors.education.message}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">Passout Year *</label>
              <select
                className="form-select"
                {...register('year', { required: 'Passout year is required' })}
              >
                <option value="">Select passout year</option>
                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                <option value={new Date().getFullYear() + 1}>{new Date().getFullYear() + 1}</option>
                <option value={new Date().getFullYear() + 2}>{new Date().getFullYear() + 2}</option>
                <option value={new Date().getFullYear() + 3}>{new Date().getFullYear() + 3}</option>
              </select>
              {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">Subjects</label>
              <select
                className="form-select"
                {...register('subjects')}
                disabled={!education}
              >
                <option value="">Select subject</option>
                {education && educationOptions.find(opt => opt.value === education)?.subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">College/University *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your college name"
                {...register('college', { required: 'College name is required' })}
              />
              {errors.college && <p className="text-red-500 text-sm mt-1">{errors.college.message}</p>}
            </div>
          </div>
        </div>
        
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center space-x-3">
            <MapPin className="w-6 h-6 text-blue-600" />
            <span>Location Information</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">State *</label>
              <select
                className="form-select"
                {...register('state', { required: 'State is required' })}
              >
                <option value="">Select state</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state.message}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">District *</label>
              <select
                className="form-select"
                {...register('district', { required: 'District is required' })}
              >
                <option value="">Select district</option>
                {watch('state') && getDistricts(watch('state')).map(district => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
              {errors.district && <p className="text-red-500 text-sm mt-1">{errors.district.message}</p>}
              
              {watch('district') === 'Other' && (
                <div className="mt-3">
                  <label className="form-label">Please specify district *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your district name"
                    {...register('district_other', { 
                      required: watch('district') === 'Other' ? 'Please specify your district' : false 
                    })}
                  />
                  {errors.district_other && <p className="text-red-500 text-sm mt-1">{errors.district_other.message}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center space-x-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <span>Academic Performance</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">10th Standard Marks (%) *</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="form-input"
                placeholder="Enter 10th marks percentage"
                {...register('marks_10th', { 
                  required: '10th marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
              />
              {errors.marks_10th && <p className="text-red-500 text-sm mt-1">{errors.marks_10th.message}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">Degree Marks (%) *</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="form-input"
                placeholder="Enter degree marks percentage"
                {...register('marks_degree', { 
                  required: 'Degree marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
              />
              {errors.marks_degree && <p className="text-red-500 text-sm mt-1">{errors.marks_degree.message}</p>}
            </div>
          </div>
        </div>
        
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center space-x-3">
            <MessageCircle className="w-6 h-6 text-blue-600" />
            <span>How did you know about Techpuram?</span>
          </h2>
          
          <div className="form-group">
            <label className="form-label">Source of Information *</label>
            <select
              className="form-select"
              {...register('source', { required: 'Please select how you came to know about Techpuram' })}
            >
              <option value="">Select source</option>
              <option value="WhatsApp">WhatsApp</option>
              <option value="Friends">Friends</option>
              <option value="Referral">Referral</option>
              <option value="Social Media">Social Media</option>
              <option value="College Notice">College Notice</option>
              <option value="Other">Other</option>
            </select>
            {errors.source && <p className="text-red-500 text-sm mt-1">{errors.source.message}</p>}
          </div>
          
          {watch('source') === 'Other' && (
            <div className="form-group">
              <label className="form-label">Please specify</label>
              <input
                type="text"
                className="form-input"
                placeholder="Please specify how you came to know about Techpuram"
                {...register('source_other')}
              />
            </div>
          )}
        </div>
        
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center space-x-3">
            <MessageCircle className="w-6 h-6 text-blue-600" />
            <span>About Techpuram</span>
          </h2>
          
          <div className="form-group">
            <label className="form-label">What do you know about Techpuram? *</label>
            <textarea
              className="form-textarea"
              rows="4"
              placeholder="Tell us what you know about Techpuram, our mission, values, or any specific information you have about our company..."
              {...register('about_techpuram', { required: 'Please tell us what you know about Techpuram' })}
            />
            {errors.about_techpuram && <p className="text-red-500 text-sm mt-1">{errors.about_techpuram.message}</p>}
          </div>
        </div>
        
        <div className="card">
          <div className="form-group">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="form-checkbox mt-1"
                {...register('consent', { required: 'You must agree to the terms' })}
              />
              <span className="text-sm text-gray-700">
                I hereby declare that all the information provided above is true and correct to the best of my knowledge. 
                I understand that any false or misleading information may result in the rejection of my application. 
                I agree to the terms and conditions of the internship program. *
              </span>
            </label>
            {errors.consent && <p className="text-red-500 text-sm mt-1">{errors.consent.message}</p>}
          </div>
        </div>
        

        
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="glass-button w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="spinner w-5 h-5"></div>
              <span>Submitting Application...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <Send className="w-5 h-5" />
              <span>Submit Application</span>
            </div>
          )}
        </motion.button>
      </motion.form>
    </motion.div>
  );
};

export default RegistrationForm;
